/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = /*@__PURE__*/ function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var DEFAULT_OPTIONS = /*@__PURE__*/ {
    channelId: 'default',
    host: window,
    coordinatorHost: window.top,
};

var INTERNAL_TYPES = /*@__PURE__*/ {
    connect: 'connect',
    connected: 'connected',
};
var LIB_ID = /*@__PURE__*/ '@wikia/post-quecast';

function isAction(input) {
    return typeof input.type === 'string' && typeof input.timestamp === 'number';
}
function isActionOfType(input) {
    var types = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        types[_i - 1] = arguments[_i];
    }
    return types.some(function (type) { return input.type === type; });
}

function isPostMessageData(input) {
    return input.channelId && input.libId === LIB_ID && input.action && isAction(input.action);
}

function isPostQuecastEvent(input) {
    return input && input.source && input.data && isPostMessageData(input.data);
}
function isEventPublic(event) {
    return !event.data.private;
}
function isEventPrivate(event) {
    return !!event.data.private;
}
function isEventOfChannel(event, channelId) {
    return event.data.channelId === channelId;
}
function isEventExternal(event) {
    return !Object.values(INTERNAL_TYPES).some(function (type) { return event.data.action.type === type; });
}
function isEventOfType(event) {
    var types = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        types[_i - 1] = arguments[_i];
    }
    return types.some(function (type) { return event.data.action.type === type; });
}
function getEventAction(event) {
    return event.data.action;
}

function isCallbackMode(options) {
    var _a;
    return (options.host === options.coordinatorHost && !!((_a = options.coordinatorHost[LIB_ID]) === null || _a === void 0 ? void 0 : _a.callbackConnector));
}
var Connector = /*@__PURE__*/ (function () {
    function Connector() {
    }
    Connector.make = function (options) {
        if (isCallbackMode(options)) {
            return options.coordinatorHost[LIB_ID].callbackConnector;
        }
        return new PostMessageConnector({
            senderHost: options.coordinatorHost,
            listenerHost: options.host,
        });
    };
    return Connector;
}());
var CallbackConnector = /*@__PURE__*/ (function () {
    function CallbackConnector(coordinatorHost) {
        this.coordinatorHost = coordinatorHost;
        this.callbacks = [];
    }
    CallbackConnector.prototype.dispatch = function (data) {
        var _this = this;
        if (!isPostMessageData(data)) {
            throw new Error("Incorrect object type. Expected PostMessageData, but got " + data);
        }
        this.callbacks.forEach(function (cb) { return cb({ data: data, source: _this.coordinatorHost }); });
    };
    CallbackConnector.prototype.addListener = function (cb) {
        this.callbacks.push(cb);
    };
    CallbackConnector.prototype.removeListener = function (cb) {
        this.callbacks = this.callbacks.filter(function (_cb) { return _cb !== cb; });
    };
    return CallbackConnector;
}());
var PostMessageConnector = /*@__PURE__*/ (function () {
    function PostMessageConnector(options) {
        this.options = options;
        this.map = new Map();
    }
    PostMessageConnector.prototype.dispatch = function (data) {
        if (!isPostMessageData(data)) {
            throw new Error("Incorrect object type. Expected PostMessageData, but got " + data);
        }
        this.options.senderHost.postMessage(data, '*');
    };
    PostMessageConnector.prototype.addListener = function (cb) {
        var wrapper = function (event) {
            if (!isPostQuecastEvent(event)) {
                return;
            }
            cb(event);
        };
        this.map.set(cb, wrapper);
        this.options.listenerHost.addEventListener('message', wrapper);
    };
    PostMessageConnector.prototype.removeListener = function (cb) {
        var wrapper = this.map.get(cb);
        this.options.listenerHost.removeEventListener('message', wrapper);
        this.map.delete(cb);
    };
    return PostMessageConnector;
}());

var Receiver = /*@__PURE__*/ (function () {
    function Receiver(options) {
        this.callbacks = [];
        this.history = [];
        this.channelId = options.channelId;
        this.connector = Connector.make(options);
        this.setupActions();
        this.setupConnection();
    }
    Receiver.prototype.setupActions = function () {
        this.getHistory();
        this.listenEvent();
    };
    Receiver.prototype.getHistory = function () {
        var _this = this;
        var callback = function (event) {
            if (!isEventOfChannel(event, _this.channelId)) {
                return;
            }
            if (!isEventPrivate(event)) {
                return;
            }
            var action = getEventAction(event);
            if (!isActionOfType(action, INTERNAL_TYPES.connected)) {
                return;
            }
            _this.connector.removeListener(callback);
            _this.handleActions.apply(_this, action.history);
        };
        this.connector.addListener(callback);
    };
    Receiver.prototype.listenEvent = function () {
        var _this = this;
        var callback = function (event) {
            if (!isEventOfChannel(event, _this.channelId)) {
                return;
            }
            if (!isEventPublic(event)) {
                return;
            }
            var action = getEventAction(event);
            _this.handleActions(action);
        };
        this.connector.addListener(callback);
    };
    Receiver.prototype.handleActions = function () {
        var _a;
        var _this = this;
        var actions = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            actions[_i] = arguments[_i];
        }
        (_a = this.history).push.apply(_a, actions);
        actions.forEach(function (action) { return _this.callbacks.forEach(function (cb) { return cb(action); }); });
    };
    Receiver.prototype.setupConnection = function () {
        this.connector.dispatch({
            action: { type: INTERNAL_TYPES.connect, timestamp: Date.now() },
            channelId: this.channelId,
            private: true,
            libId: LIB_ID,
        });
    };
    Receiver.prototype.addListener = function (cb) {
        this.history.forEach(function (action) { return cb(action); });
        this.callbacks.push(cb);
    };
    Receiver.prototype.removeListener = function (cb) {
        this.callbacks = this.callbacks.filter(function (_cb) { return _cb !== cb; });
    };
    return Receiver;
}());

var Transmitter = /*@__PURE__*/ (function () {
    function Transmitter(_options) {
        if (_options === void 0) { _options = {}; }
        var options = __assign(__assign({}, DEFAULT_OPTIONS), _options);
        this.channelId = options.channelId;
        this.connector = Connector.make(options);
    }
    Transmitter.prototype.dispatch = function (action) {
        this.connector.dispatch({
            action: __assign(__assign({}, action), { timestamp: Date.now() }),
            channelId: this.channelId,
            private: true,
            libId: LIB_ID,
        });
    };
    return Transmitter;
}());

var Communicator = /*@__PURE__*/ (function () {
    function Communicator(_options) {
        if (_options === void 0) { _options = {}; }
        var options = __assign(__assign({}, DEFAULT_OPTIONS), _options);
        this.transmitter = new Transmitter(options);
        this.receiver = new Receiver(options);
    }
    Communicator.prototype.dispatch = function (action) {
        this.transmitter.dispatch(action);
    };
    Communicator.prototype.addListener = function (cb) {
        this.receiver.addListener(cb);
    };
    Communicator.prototype.removeListener = function (cb) {
        this.receiver.removeListener(cb);
    };
    return Communicator;
}());

var Channel = /*@__PURE__*/ (function () {
    function Channel(channelId, coordinatorHost) {
        this.channelId = channelId;
        this.connectors = new Map();
        this.history = [];
        this.connectors.set(coordinatorHost, coordinatorHost[LIB_ID].callbackConnector);
    }
    Channel.prototype.addConnection = function (connection) {
        this.ensureConnector(connection).dispatch({
            action: this.createConnectedAction(),
            private: true,
            channelId: this.channelId,
            libId: LIB_ID,
        });
    };
    Channel.prototype.createConnectedAction = function () {
        return {
            type: INTERNAL_TYPES.connected,
            history: this.history,
            timestamp: Date.now(),
        };
    };
    Channel.prototype.broadcast = function (action) {
        var _this = this;
        this.history.push(action);
        this.connectors.forEach(function (connector) {
            connector.dispatch({ action: action, channelId: _this.channelId, libId: LIB_ID });
        });
    };
    Channel.prototype.ensureConnector = function (connection) {
        if (!this.connectors.has(connection)) {
            this.connectors.set(connection, new PostMessageConnector({ senderHost: connection, listenerHost: connection }));
        }
        return this.connectors.get(connection);
    };
    return Channel;
}());

var Coordinator = /*@__PURE__*/ (function () {
    function Coordinator(coordinatorHost) {
        this.coordinatorHost = coordinatorHost;
        this.channels = new Map();
        this.callbackConnector = new CallbackConnector(coordinatorHost);
        this.postMessageConnector = new PostMessageConnector({
            senderHost: coordinatorHost,
            listenerHost: coordinatorHost,
        });
    }
    Coordinator.prototype.init = function () {
        var _this = this;
        var handleEvents = function (event) {
            if (isEventPublic(event)) {
                return;
            }
            _this.handleConnect(event);
            _this.handleBroadcast(event);
        };
        this.callbackConnector.addListener(handleEvents);
        this.postMessageConnector.addListener(handleEvents);
    };
    Coordinator.prototype.handleConnect = function (event) {
        if (!isEventOfType(event, INTERNAL_TYPES.connect)) {
            return;
        }
        var channel = this.ensureChannel(event.data.channelId);
        channel.addConnection(event.source);
    };
    Coordinator.prototype.handleBroadcast = function (event) {
        if (!isEventExternal(event)) {
            return;
        }
        var channel = this.ensureChannel(event.data.channelId);
        channel.broadcast(event.data.action);
    };
    Coordinator.prototype.ensureChannel = function (channelId) {
        if (!this.channels.has(channelId)) {
            this.channels.set(channelId, new Channel(channelId, this.coordinatorHost));
        }
        return this.channels.get(channelId);
    };
    return Coordinator;
}());

function setupPostQuecast(coordinatorHost) {
    if (coordinatorHost === void 0) { coordinatorHost = window; }
    if (!!coordinatorHost[LIB_ID]) {
        return;
    }
    var coordinator = new Coordinator(coordinatorHost);
    coordinator.init();
    coordinatorHost[LIB_ID] = coordinator;
}

export { Communicator, Transmitter, isActionOfType, setupPostQuecast };
//# sourceMappingURL=index.js.map
